import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import AppLayout from '@/components/layouts/AppLayout'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Paper from '@mui/material/Paper'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import OutlinedInput from '@mui/material/OutlinedInput'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import type { Category } from '@/types'

interface Props {
  vendor: { id: number; name: string; slug: string }
  categories: Category[]
}

interface PageProps {
  errors?: Record<string, string[]>
}

export default function AppEntryNew({ vendor, categories }: Props) {
  const { props } = usePage<PageProps>()
  const [form, setForm] = useState({
    name: '',
    description: '',
    short_description: '',
    visibility: 'public_entry',
  })
  const [categoryIds, setCategoryIds] = useState<number[]>([])
  const [logo, setLogo] = useState<File | null>(null)
  const [screenshots, setScreenshots] = useState<FileList | null>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const formData = new FormData()
    formData.append('app_entry[name]', form.name)
    formData.append('app_entry[description]', form.description)
    formData.append('app_entry[short_description]', form.short_description)
    formData.append('app_entry[visibility]', form.visibility)
    categoryIds.forEach((id) => formData.append('app_entry[category_ids][]', String(id)))
    if (logo) formData.append('app_entry[logo]', logo)
    if (screenshots) {
      Array.from(screenshots).forEach((file) => formData.append('app_entry[screenshots][]', file))
    }
    router.post(`/vendors/${vendor.slug}/app_entries`, formData)
  }

  const errors = props.errors || {}

  return (
    <AppLayout>
      <Paper sx={{ p: 4, maxWidth: 700, mx: 'auto' }}>
        <Typography variant="h5" gutterBottom>New App Entry — {vendor.name}</Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name?.join(', ')}
          />
          <TextField
            label="Short Description"
            value={form.short_description}
            onChange={(e) => setForm({ ...form, short_description: e.target.value })}
            helperText={`${form.short_description.length}/300 characters`}
            inputProps={{ maxLength: 300 }}
          />
          <TextField
            label="Description"
            multiline
            rows={6}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <FormControl>
            <InputLabel>Categories</InputLabel>
            <Select
              multiple
              value={categoryIds}
              onChange={(e) => setCategoryIds(e.target.value as number[])}
              input={<OutlinedInput label="Categories" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const cat = categories.find((c) => c.id === id)
                    return <Chip key={id} label={cat?.name} size="small" />
                  })}
                </Box>
              )}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={form.visibility === 'protected_entry'}
                onChange={(e) => setForm({ ...form, visibility: e.target.checked ? 'protected_entry' : 'public_entry' })}
              />
            }
            label="Protected (visible only to shared users)"
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>Logo</Typography>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setLogo(e.target.files?.[0] || null)}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>Screenshots</Typography>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              onChange={(e) => setScreenshots(e.target.files)}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => router.visit(`/vendors/${vendor.slug}`)}>Cancel</Button>
            <Button type="submit" variant="contained">Create Entry</Button>
          </Box>
        </Box>
      </Paper>
    </AppLayout>
  )
}
